import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useBackDismiss } from '../../hooks.js';
import { t } from '../../i18n/index.js';

/*
  Modal, ki ga je mogoče uporabljati s tipkovnico.

  Stara verzija je modale risala kot <div> z onClick — brez fokusa, brez
  Escape, brez vloge. Kdor je uporabljal tipkovnico ali bralnik zaslona,
  je ostal ujet za modalom.

  Tu je narejeno:
  - fokus se ob odprtju prestavi v modal in se ob zaprtju vrne tja, od koder
    je prišel,
  - Tab kroži znotraj modala in ne uide na stran zadaj,
  - Escape zapre,
  - stran zadaj se ne premika (na iOS-u je to posebej moteče),
  - role="dialog" aria-modal in povezan naslov.
*/

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

let openCount = 0;

export function Modal({ title, eyebrow, onClose, children, footer, size, labelledBy }) {
  const ref = useRef(null);
  const restoreTo = useRef(null);

  // Sistemski „nazaj" zapre modal, ne aplikacije.
  useBackDismiss(true, onClose);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2, 9)}`);

  const focusFirst = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const target = el.querySelector('[data-autofocus]') ?? el.querySelector(FOCUSABLE) ?? el;
    target.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    restoreTo.current = document.activeElement;
    focusFirst();

    // Zaklep premikanja strani zadaj. Štejemo, ker je lahko modal nad modalom.
    openCount += 1;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      openCount -= 1;
      if (openCount === 0) document.body.style.overflow = prev;
      restoreTo.current?.focus?.({ preventScroll: true });
    };
  }, [focusFirst]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose?.();
      return;
    }
    if (e.key !== 'Tab') return;

    const items = [...(ref.current?.querySelectorAll(FOCUSABLE) ?? [])].filter(
      (n) => n.offsetParent !== null,
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={ref}
        className={`modal${size === 'sm' ? ' modal--sm' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId.current}
        onKeyDown={onKeyDown}
        tabIndex={-1}
      >
        <div className="modal__head">
          <div className="spacer">
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h2 id={titleId.current} className="t-lg t-black">
              {title}
            </h2>
          </div>
          <button className="btn-icon" onClick={onClose} aria-label={t('Zapri')}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal__body">{children}</div>

        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
